import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Button,
  Card,
  Form,
  Input,
  Modal,
  Segmented,
  Select,
  Space,
  Spin,
  Table,
  Tooltip,
  Typography,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ColumnWidthOutlined,
  StopOutlined,
  AuditOutlined,
} from "@ant-design/icons";
import TooltipIcon from "../../common/TooltipIcon";
import capitalize from "lodash/capitalize";
import debounce from "lodash/debounce";
import cloneDeep from "lodash/cloneDeep";
import { useSelector } from "react-redux";
import {
  exchangeTokenForRolesToken,
  getRoles,
  getTargetMappings,
  getTargetIds,
  formatDropdownData,
  roleOperators as ROLE_OPERATOR_DEFINITIONS,
} from "./utils";
import "./RoleGroupCriteria.css";
import { showErrorNotification } from "../../../util";

// --- Constants ---

const EXPERIMENT_TYPE = "EXPERIMENT";

const ELEMENT = Object.freeze({
  ROLE: "role",
  FIRST_TARGET: "firstTarget",
  SECOND_TARGET: "secondTarget",
});

const SECTION = Object.freeze({
  TYPE: "type",
  ID: "id",
});

const ROLE_OPERATORS = Object.freeze({
  EQUALS: "Eq",
  NOT_EQUALS: "Neq",
  BETWEEN: "Between",
  IN: "In",
});

const ROLE_EXPERIMENT_OPERATORS = ROLE_OPERATOR_DEFINITIONS.filter(
  (op) => op.value !== "Between"
);

const EMPTY_ROLE_CRITERIA = Object.freeze({
  role: "",
  operation: ROLE_OPERATORS.EQUALS,
  firstTarget: { type: "", id: "", operator: "" },
  secondTarget: { type: "", id: "", operator: "" },
  roleExperiment: {
    programOperation: "",
    experimentProgram: [],
    statusOperation: "",
    experimentStatus: [],
  },
});

const INITIAL_TOUCHED = Object.freeze({
  role: false,
  firstTargetType: false,
  firstTargetId: false,
  firstTargetIdMin: false,
  firstTargetIdMax: false,
  secondTargetType: false,
  secondTargetId: false,
  secondTargetIdMin: false,
  secondTargetIdMax: false,
});

const VALIDATION_TYPE = Object.freeze({
  ID: "id",
  MIN_ID: "minId",
  MAX_ID: "maxId",
  ROLE: "role",
});

/**
 * RoleGroupCriteria component
 *
 * Allows users to create, edit, and delete dynamic group role criteria.
 * Supports selection of role, target type, and target ID with advanced operator logic.
 * Handles validation, select search, and backend conversion.
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.canModifyGroup - Whether the user can modify the group.
 * @param {Array|Object} props.roleCriteria - The current role criteria from the backend.
 * @param {Function} props.handleUpdate - Callback to update criteria in the parent.
 * @param {boolean} props.loading - Loading state for the component.
 */
const andOrOptions = [
  { label: "AND", value: "And" },
  { label: "OR", value: "Or" },
];

const RoleGroupCriteria = ({
  canModifyGroup,
  roleCriteria,
  handleUpdate,
  loading,
  categoryOperator,
  onCategoryOperatorChange,
}) => {
  // --- State Variables ---
  const [criteriaList, setCriteriaList] = useState([]);
  const [currentCriteria, setCurrentCriteria] = useState(EMPTY_ROLE_CRITERIA);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [editIndex, setEditIndex] = useState(null);
  const [showSecondTarget, setShowSecondTarget] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [rolesToken, setRolesToken] = useState(null);
  const [touched, setTouched] = useState(INITIAL_TOUCHED);
  const keycloakToken = useSelector((state) => state.keycloak.instance.token);
  const [selectOptions, setSelectOptions] = useState({
    roles: [],
    firstTargetTypes: [],
    firstTargetIds: [],
    firstTargetMinIds: [],
    firstTargetMaxIds: [],
    secondTargetTypes: [],
    secondTargetIds: [],
    secondTargetMinIds: [],
    secondTargetMaxIds: [],
  });
  const [selectLoading, setSelectLoading] = useState({
    roles: false,
    firstTargetTypes: false,
    firstTargetIds: false,
    firstTargetMinIds: false,
    firstTargetMaxIds: false,
    secondTargetTypes: false,
    secondTargetIds: false,
    secondTargetMinIds: false,
    secondTargetMaxIds: false,
  });
  const [searchQueries, setSearchQueries] = useState({
    roles: "",
    firstTargetTypes: "",
    firstTargetIds: "",
    firstTargetMinIds: "",
    firstTargetMaxIds: "",
    secondTargetTypes: "",
    secondTargetIds: "",
    secondTargetMinIds: "",
    secondTargetMaxIds: "",
  });
  const debouncedSearchRef = useRef();

  // --- Fetch Functions ---

  /**
   * Fetches role options for the select, supporting wildcard and exact search.
   *
   * @param {string} [search=""] - The search string.
   * @param {boolean} [isExact=false] - Whether to search for an exact match.
   * @returns {Promise<Array>} The formatted select options.
   */
  const fetchRoleOptions = useCallback(
    async (search = "", isExact = false) => {
      if (!rolesToken) return [];
      try {
        setSelectLoading((prev) => ({ ...prev, roles: true }));
        let params = {};
        if (search) {
          params = {
            search: [
              {
                field: "name",
                op: ":",
                value: isExact ? search : `%${search}%`,
              },
            ],
          };
        }
        const roles = await getRoles(rolesToken, params);
        return formatDropdownData(roles, "name", "shortEnglishDescription");
      } catch (error) {
        showErrorNotification("Failed to Fetch Roles", error.message);

        return [];
      } finally {
        setSelectLoading((prev) => ({ ...prev, roles: false }));
      }
    },
    [rolesToken]
  );

  /**
   * Fetches first target type options for the select.
   *
   * @param {string} [search=""] - The search string.
   * @param {string} [selectedRole=""] - The selected role.
   * @param {boolean} [isExact=false] - Whether to search for an exact match.
   * @returns {Promise<Array>} The formatted select options.
   */
  const fetchFirstTargetTypeOptions = useCallback(
    async (search = "", selectedRole = "", isExact = false) => {
      if (!rolesToken || !selectedRole) return [];
      try {
        setSelectLoading((prev) => ({ ...prev, firstTargetTypes: true }));
        let params = {};
        if (search) {
          params = {
            search: [
              {
                field: "target1Type",
                op: ":",
                value: isExact ? search : `%${search}%`,
              },
            ],
          };
        }
        const targetMappings = await getTargetMappings(
          rolesToken,
          selectedRole,
          true,
          params
        );
        const uniqueTypes = [
          ...new Set(targetMappings.map((m) => m.target1Type)),
        ].map((type) => ({ target1Type: type }));
        return formatDropdownData(uniqueTypes, "target1Type", null);
      } catch (error) {
        showErrorNotification(
          "Failed to Fetch First Target Types",
          error.message
        );

        return [];
      } finally {
        setSelectLoading((prev) => ({ ...prev, firstTargetTypes: false }));
      }
    },
    [rolesToken]
  );

  /**
   * Fetches second target type options for the select.
   *
   * @param {string} [search=""] - The search string.
   * @param {string} [selectedRole=""] - The selected role.
   * @param {string} [firstTargetTypeValue=""] - The selected first target type.
   * @param {boolean} [isExact=false] - Whether to search for an exact match.
   * @returns {Promise<Array>} The formatted select options.
   */
  const fetchSecondTargetTypeOptions = useCallback(
    async (
      search = "",
      selectedRole = "",
      firstTargetTypeValue = "",
      isExact = false
    ) => {
      if (!rolesToken || !selectedRole || !firstTargetTypeValue) return [];
      try {
        setSelectLoading((prev) => ({ ...prev, secondTargetTypes: true }));
        const params = {
          search: [
            { field: "target1Type", op: ":", value: firstTargetTypeValue },
          ],
        };
        if (search) {
          params.search.push({
            field: "target2Type",
            op: ":",
            value: isExact ? search : `%${search}%`,
            groupOp: "AND",
          });
        }
        const targetMappings = await getTargetMappings(
          rolesToken,
          selectedRole,
          false,
          params
        );
        const uniqueTypes = [
          ...new Set(targetMappings.map((m) => m.target2Type).filter(Boolean)),
        ].map((type) => ({ target2Type: type }));
        return formatDropdownData(uniqueTypes, "target2Type", null);
      } catch (error) {
        showErrorNotification(
          "Failed to Fetch Second Target Types",
          error.message
        );

        return [];
      } finally {
        setSelectLoading((prev) => ({ ...prev, secondTargetTypes: false }));
      }
    },
    [rolesToken]
  );

  /**
   * Fetches target ID options for the select, supporting BETWEEN, IN, and other operators.
   *
   * @param {boolean} isFirstTarget - True if fetching for first target, false for second.
   * @param {string} [search=""] - The search string.
   * @param {string} [targetTypeValue=""] - The selected target type.
   * @param {string|null} [filterGreaterThan=null] - Filter for min value (BETWEEN).
   * @param {string|null} [filterLessThan=null] - Filter for max value (BETWEEN).
   * @param {boolean} [isExact=false] - Whether to search for an exact match.
   * @returns {Promise<Array>} The formatted select options.
   */
  const fetchTargetIdOptions = useCallback(
    async (
      isFirstTarget,
      search = "",
      targetTypeValue = "",
      filterGreaterThan = null,
      filterLessThan = null,
      isExact = false,
      loadingKeyOverride = null
    ) => {
      if (!rolesToken || !targetTypeValue) return [];
      const loadingKey =
        loadingKeyOverride ||
        (isFirstTarget ? "firstTargetIds" : "secondTargetIds");
      try {
        setSelectLoading((prev) => ({ ...prev, [loadingKey]: true }));
        const searchConditions = [
          { field: "type", op: ":", value: targetTypeValue },
        ];
        if (search) {
          searchConditions.push({
            field: "id",
            op: ":",
            value: isExact ? search : `%${search}%`,
            groupOp: "AND",
          });
        }
        const params = {
          search: searchConditions,
          filterGreaterThan: filterGreaterThan,
          filterLessThan: filterLessThan,
        };

        const results = await getTargetIds(rolesToken, params);
        return formatDropdownData(results, "id", "longEnglishDescription");
      } catch (error) {
        showErrorNotification(
          `Failed to Fetch ${isFirstTarget ? "First" : "Second"} Target IDs`,
          error.message
        );

        return [];
      } finally {
        setSelectLoading((prev) => ({ ...prev, [loadingKey]: false }));
      }
    },
    [rolesToken]
  );

  // --- Utility Functions ---

  /**
   * Ensures selected values are present in the select options.
   * Used to guarantee that selected values are always visible, even if not in the fetched results.
   *
   * @param {Array} options - The select options.
   * @param {string|string[]} selectedValues - The selected value(s).
   * @returns {Array} The merged options.
   */
  const mergeSelectedIntoOptions = useCallback((options, selectedValues) => {
    const newOptions = [...options];
    const optionValues = new Set(newOptions.map((opt) => opt.value));
    const valuesToEnsure = Array.isArray(selectedValues)
      ? selectedValues
      : selectedValues
      ? [selectedValues]
      : [];

    valuesToEnsure.forEach((selectedValue) => {
      if (
        selectedValue !== undefined &&
        selectedValue !== null &&
        selectedValue !== "" &&
        !optionValues.has(selectedValue)
      ) {
        let label = selectedValue;
        let description = "";
        newOptions.push({
          value: selectedValue,
          label,
          description,
          disabled: false,
        });
      }
    });
    return newOptions;
  }, []);

  /**
   * Resets all modal-related state and closes the modal.
   *
   * @param {Function} setOpenModalFunc - The setter function to close the modal.
   */
  const cleanAddEditModalClose = (setOpenModalFunc) => {
    setCurrentCriteria(EMPTY_ROLE_CRITERIA);
    setSelectOptions({
      roles: [],
      firstTargetTypes: [],
      firstTargetIds: [],
      firstTargetMinIds: [],
      firstTargetMaxIds: [],
      secondTargetTypes: [],
      secondTargetIds: [],
      secondTargetMinIds: [],
      secondTargetMaxIds: [],
    });
    setSearchQueries({
      roles: "",
      firstTargetTypes: "",
      firstTargetIds: "",
      firstTargetMinIds: "",
      firstTargetMaxIds: "",
      secondTargetTypes: "",
      secondTargetIds: "",
      secondTargetMinIds: "",
      secondTargetMaxIds: "",
    });
    setTouched(INITIAL_TOUCHED);
    setValidationErrors({});
    setShowSecondTarget(false);
    if (setOpenModalFunc) setOpenModalFunc(false);
    setEditIndex(null);
  };

  // --- Conversion Functions ---

  /**
   * Converts backend criteria format to local UI format.
   *
   * @param {Array|Object} beCriteria - The backend criteria.
   * @returns {Array} The local criteria list.
   */
  const convertBackendToLocalCriteria = useCallback((beCriteria) => {
    const isRoleCriteriaObject = (obj) =>
      obj && typeof obj === "object" && "role" in obj && "operation" in obj;

    const transformCriterion = (criterion) => {
      const local = cloneDeep(EMPTY_ROLE_CRITERIA);
      local.role = criterion.role || "";
      local.operation = criterion.operation || ROLE_OPERATORS.EQUALS;
      ["firstTarget", "secondTarget"].forEach((targetKey) => {
        const beTarget = criterion[targetKey];
        if (beTarget && typeof beTarget === "object") {
          local[targetKey].type = beTarget.type || "";
          local[targetKey].operator = beTarget.operation || "";
          if (beTarget.id !== undefined && beTarget.id !== null) {
            local[targetKey].id =
              local[targetKey].operator === ROLE_OPERATORS.IN ||
              local[targetKey].operator === ROLE_OPERATORS.BETWEEN
                ? Array.isArray(beTarget.id)
                  ? beTarget.id
                  : [beTarget.id]
                : Array.isArray(beTarget.id)
                ? beTarget.id[0] || ""
                : beTarget.id;
            if (
              local[targetKey].operator === ROLE_OPERATORS.BETWEEN &&
              !Array.isArray(local[targetKey].id)
            ) {
              local[targetKey].id = [local[targetKey].id, ""];
            } else if (
              local[targetKey].operator === ROLE_OPERATORS.BETWEEN &&
              local[targetKey].id.length === 1
            ) {
              local[targetKey].id.push("");
            }
          } else {
            local[targetKey].id =
              local[targetKey].operator === ROLE_OPERATORS.IN ||
              local[targetKey].operator === ROLE_OPERATORS.BETWEEN
                ? []
                : "";
          }
        }
      });
      if (criterion.roleExperiment) {
        const toArray = (val) => {
          if (Array.isArray(val)) {
            return val.flatMap((v) =>
              typeof v === "string"
                ? v
                    .split(/[,;]+/)
                    .map((s) => s.trim())
                    .filter(Boolean)
                : [v]
            );
          }
          if (typeof val === "string") {
            return val
              .split(/[,;]+/)
              .map((s) => s.trim())
              .filter(Boolean);
          }
          return val ? [val] : [];
        };
        local.roleExperiment = {
          programOperation: criterion.roleExperiment.programOperation || "",
          experimentProgram: toArray(
            criterion.roleExperiment.experimentProgram
          ),
          statusOperation: criterion.roleExperiment.statusOperation || "",
          experimentStatus: toArray(criterion.roleExperiment.experimentStatus),
        };
      } else {
        local.roleExperiment = {
          programOperation: "",
          experimentProgram: [],
          statusOperation: "",
          experimentStatus: [],
        };
      }
      return local;
    };

    if (!beCriteria) return [];
    if (Array.isArray(beCriteria)) {
      return beCriteria.map(transformCriterion);
    }
    if (
      (beCriteria.operator === "Or" || beCriteria.operator === "And") &&
      Array.isArray(beCriteria.conditions)
    ) {
      return beCriteria.conditions
        .filter(isRoleCriteriaObject)
        .map(transformCriterion);
    }
    if (isRoleCriteriaObject(beCriteria)) {
      return [transformCriterion(beCriteria)];
    }
    console.warn(
      "[RoleGroupCriteria] Unexpected roleCriteria format from backend:",
      beCriteria
    );
    return [];
  }, []);

  /**
   * Converts local UI criteria format to backend format.
   * Handles IN with single value, BETWEEN, and defaulting to EQUALS as needed.
   *
   * @param {Array} localCriteriaList - The local criteria list.
   * @returns {Object|null} The backend criteria object or null.
   */
  const convertLocalToBackendCriteria = (localCriteriaList) => {
    if (!localCriteriaList || localCriteriaList.length === 0) return null;

    const convertToBackendCriterion = (criterion) => {
      // Operation defaults to Equals
      const backend = {
        role: criterion.role,
        operation: criterion.operation || ROLE_OPERATORS.EQUALS,
      };
      ["firstTarget", "secondTarget"].forEach((targetKey) => {
        const { type, id, operator } = criterion[targetKey];
        if (type || (id && operator)) {
          let backendId = id;
          let backendOp = operator || ROLE_OPERATORS.EQUALS;

          // Convert IN with only one value to EQUALS
          if (operator === ROLE_OPERATORS.IN) {
            const arr = Array.isArray(id)
              ? id.filter((val) => val)
              : id
              ? [id]
              : [];
            if (arr.length === 1) {
              backendOp = ROLE_OPERATORS.EQUALS;
              backendId = [arr[0]];
            } else {
              backendId = arr;
            }
          } else if (operator === ROLE_OPERATORS.BETWEEN) {
            backendId = Array.isArray(id)
              ? id.filter((val) => val)
              : id
              ? [id]
              : [];
          } else {
            backendId = id ? [String(id)] : [];
          }

          if (backendOp === ROLE_OPERATORS.BETWEEN && backendId.length !== 2) {
            return;
          }

          if (type && backendId.length === 0 && !operator) {
            backend[targetKey] = {
              type,
              operation: ROLE_OPERATORS.EQUALS,
              id: [],
            };
          } else if (type || backendId.length > 0) {
            backend[targetKey] = {
              type: type || "",
              id: backendId,
              operation: backendOp,
            };
          }
        }
      });
      const isExperimentCriterion =
        criterion.firstTarget?.type === EXPERIMENT_TYPE;
      if (isExperimentCriterion && criterion.roleExperiment) {
        const {
          programOperation,
          experimentProgram,
          statusOperation,
          experimentStatus,
        } = criterion.roleExperiment;
        if (programOperation || statusOperation) {
          backend.roleExperiment = {
            ...(programOperation && {
              programOperation,
              experimentProgram: experimentProgram || [],
            }),
            ...(statusOperation && {
              statusOperation,
              experimentStatus: experimentStatus || [],
            }),
          };
        }
      }
      return backend;
    };

    const conditions = localCriteriaList
      .map(convertToBackendCriterion)
      .filter(Boolean);
    if (conditions.length === 0) return null;
    if (conditions.length === 1) return conditions[0];
    return { operator: "Or", conditions };
  };

  // --- Validation Functions ---

  /**
   * Returns a validation error message for a given element and validation type, or an empty string if valid.
   *
   * @param {string} element - The criteria element (role, firstTarget, secondTarget).
   * @param {string} validationType - The type of validation (role, id, minId, maxId).
   * @returns {string} The error message or empty string.
   */
  const getValidationErrorForTarget = useCallback(
    (element, validationType) => {
      const data =
        element === ELEMENT.ROLE ? currentCriteria : currentCriteria[element];
      const targetOperator =
        element !== ELEMENT.ROLE ? currentCriteria[element]?.operator : null;
      const targetId =
        element !== ELEMENT.ROLE ? currentCriteria[element]?.id : null;

      switch (validationType) {
        case VALIDATION_TYPE.ROLE:
          if (!data.role) return "Role is required.";
          break;
        case VALIDATION_TYPE.ID:
          if (
            targetOperator &&
            targetOperator !== ROLE_OPERATORS.BETWEEN &&
            (!targetId ||
              (Array.isArray(targetId) && targetId.length === 0) ||
              (Array.isArray(targetId) && targetId.some((v) => !v)))
          ) {
            return "Please select at least one ID for the chosen operator.";
          }
          break;
        case VALIDATION_TYPE.MIN_ID:
          if (
            targetOperator === ROLE_OPERATORS.BETWEEN &&
            (!targetId || !Array.isArray(targetId) || !targetId[0])
          ) {
            return "Please select a minimum value.";
          }
          break;
        case VALIDATION_TYPE.MAX_ID:
          if (
            targetOperator === ROLE_OPERATORS.BETWEEN &&
            (!targetId || !Array.isArray(targetId) || !targetId[1])
          ) {
            return "Please select a maximum value.";
          }
          break;
        default:
          return "";
      }
      return "";
    },
    [currentCriteria]
  );

  /**
   * Returns true if the given criteria object is valid, false otherwise.
   *
   * @param {Object} criteria - The criteria object.
   * @returns {boolean} Whether the criteria is valid.
   */
  const isValidCriteria = (criteria) => {
    if (!criteria.role || !criteria.operation) return false;
    const targets = [criteria.firstTarget, criteria.secondTarget];
    for (const target of targets) {
      if (target.id && !target.operator && target.type) {
      } else if (
        target.operator &&
        !target.id &&
        !(Array.isArray(target.id) && target.operator === ROLE_OPERATORS.IN)
      ) {
        if (
          target.operator === ROLE_OPERATORS.BETWEEN &&
          (!Array.isArray(target.id) ||
            target.id.length !== 2 ||
            !target.id[0] ||
            !target.id[1])
        )
          return false;
        if (
          target.operator !== ROLE_OPERATORS.BETWEEN &&
          (!target.id || (Array.isArray(target.id) && target.id.length === 0))
        )
          return false;
      } else if (target.operator) {
        if (!target.type && target.id) return false;
        switch (target.operator) {
          case ROLE_OPERATORS.IN:
            if (
              !Array.isArray(target.id) ||
              target.id.length === 0 ||
              !target.id.every((item) => item)
            )
              return false;
            break;
          case ROLE_OPERATORS.BETWEEN:
            if (
              !Array.isArray(target.id) ||
              target.id.length !== 2 ||
              !target.id[0] ||
              !target.id[1]
            )
              return false;
            break;
          default:
            if (typeof target.id !== "string" || !target.id.trim()) {
              if (
                Array.isArray(target.id) &&
                (target.id.length === 0 ||
                  !target.id[0] ||
                  !String(target.id[0]).trim())
              )
                return false;
              if (
                !Array.isArray(target.id) &&
                (target.id === "" ||
                  target.id === null ||
                  target.id === undefined)
              )
                return false;
            }
            break;
        }
      }
    }
    return true;
  };

  // --- Rendering Functions ---

  /**
   * Renders the Target ID cell for the table, showing operator icons and values as needed.
   *
   * @param {string} element - The criteria element.
   * @param {Object} criteriaItem - The criteria item.
   * @returns {JSX.Element|string} The rendered cell content.
   */
  const renderTargetIdCell = (element, criteriaItem) => {
    const { operator, id } = criteriaItem[element];

    if (
      !operator ||
      !id ||
      (Array.isArray(id) && id.length === 0) ||
      (Array.isArray(id) && id.every((v) => !v))
    ) {
      return "";
    }

    const displayId = Array.isArray(id) ? id.join(", ") : id;
    switch (operator) {
      case ROLE_OPERATORS.EQUALS:
        return displayId;
      case ROLE_OPERATORS.NOT_EQUALS:
        return (
          <>
            <Tooltip title="Not Equals" mouseEnterDelay>
              <StopOutlined style={{ color: "#ff4d4f" }} />
            </Tooltip>{" "}
            {displayId}
          </>
        );
      case ROLE_OPERATORS.BETWEEN:
        return Array.isArray(id) && id.length === 2 ? (
          <>
            {id[0]}{" "}
            <Tooltip title="Between" mouseEnterDelay>
              <ColumnWidthOutlined style={{ color: "#1890ff" }} />
            </Tooltip>{" "}
            {id[1]}
          </>
        ) : (
          displayId
        );
      case ROLE_OPERATORS.IN:
        return displayId;
      default:
        return displayId;
    }
  };

  /**
   * Renders a single option for an Ant Design Select component, including tooltip and description if present.
   *
   * @param {Object} option - The option object containing value, label, description, and disabled.
   * @returns {Object} The formatted option for Ant Design Select.
   */
  const renderSelectItem = (option) => {
    const hasDescription = !!option.description;
    return {
      key: option.value,
      value: option.value,
      disabled: option.disabled,
      label: (
        <Tooltip
          title={
            hasDescription
              ? `${option.label} - ${option.description}`
              : option.label
          }
          mouseEnterDelay={1}
        >
          <span className="select-ellipsis">
            {option.label}
            {hasDescription && (
              <span className="select-ellipsis-desc">
                {" - "}
                {option.description}
              </span>
            )}
          </span>
        </Tooltip>
      ),
    };
  };

  /**
   * Renders a single-value Select input wrapped in a Form.Item, with validation and search support.
   *
   * @param {Object} params - The parameters for the Select.
   * @param {*} params.value - The current value.
   * @param {string} params.placeholder - The placeholder text.
   * @param {boolean} params.loading - Whether the Select is loading.
   * @param {Array} params.options - The options to display.
   * @param {string} params.searchValue - The current search value.
   * @param {Function} params.onSearch - Handler for search input.
   * @param {Function} params.onChange - Handler for value change.
   * @param {Function} params.onBlur - Handler for blur event.
   * @param {string} params.touchedKey - Key for touched state.
   * @param {string} params.errorKey - Key for error state.
   * @param {boolean} [params.allowClear=true] - Whether to allow clearing the selection.
   * @param {string} [params.mode] - The Select mode (e.g., "multiple").
   * @param {boolean} [params.disabled] - Whether the Select is disabled.
   * @returns {JSX.Element} The rendered Form.Item with Select.
   */
  const renderSingleValueSelect = ({
    value,
    placeholder,
    loading,
    options,
    searchValue,
    onSearch,
    onChange,
    onBlur,
    touchedKey,
    errorKey,
    allowClear = true,
    mode,
    disabled,
  }) => (
    <Form.Item
      style={{ flex: 2, marginBottom: 0 }}
      validateStatus={
        touched[touchedKey] && validationErrors[errorKey] ? "error" : ""
      }
      help={touched[touchedKey] && validationErrors[errorKey]}
    >
      <Select
        placeholder={placeholder}
        showSearch
        allowClear={allowClear}
        mode={mode}
        options={options.map(renderSelectItem)}
        value={value === "" ? undefined : value}
        loading={loading}
        disabled={disabled}
        searchValue={searchValue}
        onSearch={onSearch}
        onChange={onChange}
        onBlur={onBlur}
        filterOption={false}
        notFoundContent={loading ? <Spin size="small" /> : "No results"}
      />
    </Form.Item>
  );

  /**
   * Renders the value Select(s) for any target field (role, type, id), supporting all operators.
   * Each value Select is wrapped in its own Form.Item for error/helper display.
   *
   * @param {string} element - The criteria element (role, firstTarget, secondTarget).
   * @param {string|null} section - The section (type, id) or null.
   * @returns {JSX.Element} The rendered value Select(s).
   */
  const renderTargetValues = (element, section = null) => {
    let op,
      value,
      type,
      selectKey,
      touchedKey,
      errorKey,
      placeholder,
      isMulti,
      isBetween,
      isRole,
      isType,
      isId;
    isRole = element === ELEMENT.ROLE && !section;
    isType = !isRole && section === SECTION.TYPE;
    isId = !isRole && section === SECTION.ID;

    let disabled = false;
    if (isRole) {
      disabled = false;
      op = currentCriteria.operation;
      value = currentCriteria.role;
      selectKey = "roles";
      touchedKey = "role";
      errorKey = "role";
      placeholder = "Select Role";
      isMulti = false;
      isBetween = false;
    } else if (isType) {
      op = null;
      value = currentCriteria[element].type;
      selectKey = `${element}Types`;
      touchedKey = `${element}Type`;
      errorKey = touchedKey;
      placeholder =
        element === ELEMENT.FIRST_TARGET
          ? showSecondTarget
            ? "Select 1st Target Type"
            : "Select Target Type"
          : "Select 2nd Target Type";
      isMulti = false;
      isBetween = false;
      if (element === ELEMENT.FIRST_TARGET && !currentCriteria.role)
        disabled = true;
      if (
        element === ELEMENT.SECOND_TARGET &&
        !currentCriteria.firstTarget.type
      )
        disabled = true;
    } else if (isId) {
      op = currentCriteria[element].operator;
      type = currentCriteria[element].type;
      value = currentCriteria[element].id;
      selectKey = `${element}Ids`;
      touchedKey =
        element === ELEMENT.FIRST_TARGET ? "firstTargetId" : "secondTargetId";
      errorKey = touchedKey;
      placeholder = "Select value";
      isMulti = op === ROLE_OPERATORS.IN;
      isBetween = op === ROLE_OPERATORS.BETWEEN;
      if (!type || !op) disabled = true;
    }

    if (isRole || isType) {
      return renderSingleValueSelect({
        value,
        placeholder,
        loading: selectLoading[selectKey],
        options: selectOptions[selectKey],
        searchValue: searchQueries[selectKey],
        onSearch: (q) => handleSearchChange(element, section, q),
        onChange: (val) => handleTargetValuesChange(element, section, val),
        onBlur: () => setTouched((prev) => ({ ...prev, [touchedKey]: true })),
        touchedKey,
        errorKey,
        allowClear: true,
        mode: undefined,
        disabled,
      });
    }

    if (isId) {
      if (isBetween) {
        const minKey =
          element === ELEMENT.FIRST_TARGET
            ? "firstTargetMinIds"
            : "secondTargetMinIds";
        const maxKey =
          element === ELEMENT.FIRST_TARGET
            ? "firstTargetMaxIds"
            : "secondTargetMaxIds";
        return (
          <div style={{ display: "flex", gap: 8 }}>
            {renderSingleValueSelect({
              value: Array.isArray(value) ? value[0] : "",
              placeholder: "Min",
              loading: selectLoading[minKey],
              options: selectOptions[minKey],
              searchValue: searchQueries[minKey],
              onSearch: (q) =>
                handleSearchChange(element, section, q, { isMin: true }),
              onChange: (val) =>
                handleTargetValuesChange(element, section, [
                  val,
                  Array.isArray(value) ? value[1] : "",
                ]),
              onBlur: () =>
                setTouched((prev) => ({
                  ...prev,
                  [element === ELEMENT.FIRST_TARGET
                    ? "firstTargetIdMin"
                    : "secondTargetIdMin"]: true,
                })),
              touchedKey:
                element === ELEMENT.FIRST_TARGET
                  ? "firstTargetIdMin"
                  : "secondTargetIdMin",
              errorKey:
                element === ELEMENT.FIRST_TARGET
                  ? "firstTargetIdMin"
                  : "secondTargetIdMin",
              allowClear: true,
              mode: undefined,
              disabled,
            })}
            {renderSingleValueSelect({
              value: Array.isArray(value) ? value[1] : "",
              placeholder: "Max",
              loading: selectLoading[maxKey],
              options: selectOptions[maxKey],
              searchValue: searchQueries[maxKey],
              onSearch: (q) =>
                handleSearchChange(element, section, q, { isMax: true }),
              onChange: (val) =>
                handleTargetValuesChange(element, section, [
                  Array.isArray(value) ? value[0] : "",
                  val,
                ]),
              onBlur: () =>
                setTouched((prev) => ({
                  ...prev,
                  [element === ELEMENT.FIRST_TARGET
                    ? "firstTargetIdMax"
                    : "secondTargetIdMax"]: true,
                })),
              touchedKey:
                element === ELEMENT.FIRST_TARGET
                  ? "firstTargetIdMax"
                  : "secondTargetIdMax",
              errorKey:
                element === ELEMENT.FIRST_TARGET
                  ? "firstTargetIdMax"
                  : "secondTargetIdMax",
              allowClear: true,
              mode: undefined,
              disabled,
            })}
          </div>
        );
      }
      if (isMulti) {
        return renderSingleValueSelect({
          value: value || [],
          placeholder,
          loading: selectLoading[selectKey],
          options: selectOptions[selectKey],
          searchValue: searchQueries[selectKey],
          onSearch: (q) => handleSearchChange(element, section, q),
          onChange: (val) => handleTargetValuesChange(element, section, val),
          onBlur: () => setTouched((prev) => ({ ...prev, [touchedKey]: true })),
          touchedKey,
          errorKey,
          allowClear: true,
          mode: "multiple",
          disabled,
        });
      }
      return renderSingleValueSelect({
        value,
        placeholder,
        loading: selectLoading[selectKey],
        options: selectOptions[selectKey],
        searchValue: searchQueries[selectKey],
        onSearch: (q) => handleSearchChange(element, section, q),
        onChange: (val) => handleTargetValuesChange(element, section, val),
        onBlur: () => setTouched((prev) => ({ ...prev, [touchedKey]: true })),
        touchedKey,
        errorKey,
        allowClear: true,
        mode: undefined,
        disabled,
      });
    }
    return null;
  };

  /**
   * Renders the form fields for role, target type, and target ID selection.
   * Handles all operator and validation logic for each field.
   *
   * @param {string} element - The criteria element.
   * @param {string|null} section - The section (type, id) or null.
   * @returns {JSX.Element} The rendered form fields.
   */
  const renderTargetInputs = (element, section = null) => {
    const isRoleElement = element === ELEMENT.ROLE;
    const isTypeSection = section === SECTION.TYPE;
    const isIdSection = section === SECTION.ID;

    let labelText = "";
    let isRequired = false;

    if (isRoleElement) {
      labelText = "Role";
      isRequired = true;
    } else if (isTypeSection) {
      labelText =
        element === ELEMENT.FIRST_TARGET
          ? showSecondTarget
            ? "1st Target Type"
            : "Target Type"
          : "2nd Target Type";
      isRequired = false;
    } else if (isIdSection) {
      labelText =
        element === ELEMENT.FIRST_TARGET
          ? showSecondTarget
            ? "1st Target ID"
            : "Target ID"
          : "2nd Target ID";
      isRequired = false;
    }

    let operatorSelect = null;
    if (isRoleElement) {
      operatorSelect = (
        <Form.Item style={{ flexShrink: 0, marginBottom: 0 }}>
          <Select
            style={{ width: 160 }}
            placeholder="Select Operator"
            allowClear={false}
            options={[
              { label: "Equals", value: ROLE_OPERATORS.EQUALS },
              { label: "Not Equals", value: ROLE_OPERATORS.NOT_EQUALS },
            ]}
            value={currentCriteria.operation || ROLE_OPERATORS.EQUALS}
            onChange={(newOp) => handleRoleOperatorChange(newOp)}
          />
        </Form.Item>
      );
    } else if (isIdSection) {
      const op = currentCriteria[element].operator;
      operatorSelect = (
        <Form.Item style={{ flexShrink: 0, marginBottom: 0 }}>
          <Select
            style={{ width: 160 }}
            placeholder="Select Operator"
            allowClear
            options={ROLE_OPERATOR_DEFINITIONS}
            value={op === "" ? undefined : op}
            onChange={(newOp) => handleTargetOperatorChange(element, newOp)}
            disabled={!currentCriteria[element].type}
          />
        </Form.Item>
      );
    }

    return (
      <Form.Item
        label={labelText}
        required={isRequired}
        style={{ marginBottom: 16 }}
      >
        <div style={{ display: "flex", gap: 8 }}>
          {operatorSelect}
          <div style={{ flex: 2 }}>{renderTargetValues(element, section)}</div>
        </div>
      </Form.Item>
    );
  };

  const renderExperimentProgramStatus = () => {
    const isExperiment = currentCriteria.firstTarget.type === EXPERIMENT_TYPE;
    if (!isExperiment) return null;

    const {
      programOperation,
      experimentProgram,
      statusOperation,
      experimentStatus,
    } = currentCriteria.roleExperiment;

    return (
      <Card
        size="small"
        style={{ marginBottom: 16, backgroundColor: "#fafafa" }}
        title={
          <span>
            Experiment Program &amp; Status
            <TooltipIcon
              size={14}
              title="This is only relevant if the role target is EXPERIMENT"
            />
          </span>
        }
      >
        <Form.Item label="Program" style={{ marginBottom: 12 }}>
          <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
            <Select
              style={{ width: 160, flexShrink: 0 }}
              placeholder="Select Operator"
              allowClear
              options={ROLE_EXPERIMENT_OPERATORS}
              value={programOperation || undefined}
              onChange={(val) =>
                handleExperimentChange("programOperation", val ?? "")
              }
            />
            {EXPERIMENT_SINGLE_VALUE_OPERATORS.includes(programOperation) ? (
              <Input
                style={{ flex: 2 }}
                placeholder="Enter a program value (e.g. LHC)"
                value={experimentProgram[0] || ""}
                onChange={(e) =>
                  handleExperimentChange(
                    "experimentProgram",
                    e.target.value ? [e.target.value] : []
                  )
                }
                disabled={!programOperation}
              />
            ) : (
              <Select
                mode="tags"
                style={{ flex: 2 }}
                placeholder="Enter program values (e.g. LHC)"
                value={experimentProgram}
                tokenSeparators={[",", ";"]}
                onChange={(val) =>
                  handleExperimentChange("experimentProgram", val)
                }
                disabled={!programOperation}
              />
            )}
          </div>
        </Form.Item>
        <Form.Item label="Status" style={{ marginBottom: 0 }}>
          <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
            <Select
              style={{ width: 160, flexShrink: 0 }}
              placeholder="Select Operator"
              allowClear
              options={ROLE_EXPERIMENT_OPERATORS}
              value={statusOperation || undefined}
              onChange={(val) =>
                handleExperimentChange("statusOperation", val ?? "")
              }
            />
            {EXPERIMENT_SINGLE_VALUE_OPERATORS.includes(statusOperation) ? (
              <Input
                style={{ flex: 2 }}
                placeholder="Enter a status value (e.g. FI)"
                value={experimentStatus[0] || ""}
                onChange={(e) =>
                  handleExperimentChange(
                    "experimentStatus",
                    e.target.value ? [e.target.value] : []
                  )
                }
                disabled={!statusOperation}
              />
            ) : (
              <Select
                mode="tags"
                style={{ flex: 2 }}
                placeholder="Enter status values (e.g. FI, WD)"
                value={experimentStatus}
                tokenSeparators={[",", ";"]}
                onChange={(val) =>
                  handleExperimentChange("experimentStatus", val)
                }
                disabled={!statusOperation}
              />
            )}
          </div>
        </Form.Item>
      </Card>
    );
  };

  // --- Table Configuration ---

  /**
   * Base columns for the criteria table, used to display role and target information.
   * Each column may use a custom render function for formatting.
   * Used as the foundation for both the main and confirmation tables.
   */
  const buildBaseTableColumns = (rows) => {
    const hasSecondTarget = rows.some((r) => r.secondTarget?.type);
    return [
      { title: "Role", dataIndex: "role", key: "role" },
      {
        title: "Role Operation",
        dataIndex: "operation",
        key: "operation",
        render: (_, record) => record.operation || ROLE_OPERATORS.EQUALS,
      },
      {
        title: hasSecondTarget ? "1st Target Type" : "Target Type",
        dataIndex: "firstTargetType",
        key: "firstTargetType",
        render: (_, record) => record.firstTarget?.type || "",
      },
      {
        title: hasSecondTarget ? "1st Target ID" : "Target ID",
        dataIndex: "firstTargetId",
        key: "firstTargetId",
        render: (_, record) => renderTargetIdCell(ELEMENT.FIRST_TARGET, record),
      },
      ...(hasSecondTarget
        ? [
            {
              title: "2nd Target Type",
              dataIndex: "secondTargetType",
              key: "secondTargetType",
              render: (_, record) => record.secondTarget?.type || "",
            },
            {
              title: "2nd Target ID",
              dataIndex: "secondTargetId",
              key: "secondTargetId",
              render: (_, record) =>
                renderTargetIdCell(ELEMENT.SECOND_TARGET, record),
            },
          ]
        : []),
    ];
  };

  /**
   * Columns for the main criteria table, including actions (edit button).
   * Extends base columns with an Actions column.
   */
  const criteriaTableColumns = [
    ...buildBaseTableColumns(criteriaList),
    {
      title: "Actions",
      key: "actions",
      width: 80,
      render: (_, record, index) => (
        <Button
          icon={<EditOutlined />}
          color="primary"
          variant="filled"
          onClick={() => handleEditButtonClick(index)}
          disabled={!canModifyGroup}
        />
      ),
    },
  ];

  /**
   * Generates a dataSource array for Ant Design Table components.
   * Each entry includes a unique key and the corresponding criteria object.
   *
   * @param {number[]} [indices] - Optional array of indices to include; if omitted, includes all criteria.
   * @returns {Array<Object>} The formatted dataSource for the table.
   */
  const generateDataSource = (indices) =>
    (indices || criteriaList.map((_, i) => i)).map((index) => ({
      key: index,
      ...criteriaList[index],
    }));

  // DataSource for the main criteria table (all criteria)
  const criteriaDataSource = generateDataSource();

  // DataSource for the delete confirmation modal (only selected rows)
  const deleteConfirmationDataSource = generateDataSource(selectedRows);

  /**
   * Row selection configuration for the Ant Design Table.
   * Controls which rows are selectable and handles selection state.
   */
  const rowSelection = {
    selectedRowKeys: selectedRows,
    onChange: setSelectedRows,
    getCheckboxProps: () => ({ disabled: !canModifyGroup }),
  };

  // --- Effect Hooks ---

  /**
   * Debounced search handler for selects.
   * Performs both wildcard and exact fetches, merges results, and ensures selected values are present.
   *
   * @param {string} element - The criteria element (role, firstTarget, secondTarget).
   * @param {string|null} section - The section (type, id) or null.
   * @param {string} query - The search query.
   * @param {object} [searchContext] - Additional context for BETWEEN min/max.
   */
  useEffect(() => {
    debouncedSearchRef.current = debounce(
      async (element, section, query, searchContext) => {
        let options = [];
        let exactOptions = [];
        const selectKey = section
          ? `${element}${capitalize(section)}s`
          : `${element}s`;
        let specificDropdownKey = selectKey;

        const mergeExactFirst = (exact, wildcard) => {
          if (!exact.length) return wildcard;
          const exactVal = exact[0].value;
          const deduped = [
            exact[0],
            ...wildcard.filter((opt) => opt.value !== exactVal),
          ];
          return deduped;
        };

        if (element === ELEMENT.ROLE) {
          options = await fetchRoleOptions(query, false);
          exactOptions = query ? await fetchRoleOptions(query, true) : [];
          if (query && exactOptions.length) {
            options = mergeExactFirst(exactOptions, options);
          }
        } else if (
          element === ELEMENT.FIRST_TARGET &&
          section === SECTION.TYPE
        ) {
          options = await fetchFirstTargetTypeOptions(
            query,
            currentCriteria.role,
            false
          );
          exactOptions = query
            ? await fetchFirstTargetTypeOptions(
                query,
                currentCriteria.role,
                true
              )
            : [];
          if (query && exactOptions.length) {
            options = mergeExactFirst(exactOptions, options);
          }
        } else if (
          element === ELEMENT.SECOND_TARGET &&
          section === SECTION.TYPE
        ) {
          options = await fetchSecondTargetTypeOptions(
            query,
            currentCriteria.role,
            currentCriteria.firstTarget.type,
            false
          );
          exactOptions = query
            ? await fetchSecondTargetTypeOptions(
                query,
                currentCriteria.role,
                currentCriteria.firstTarget.type,
                true
              )
            : [];
          if (query && exactOptions.length) {
            options = mergeExactFirst(exactOptions, options);
          }
        } else if (
          (element === ELEMENT.FIRST_TARGET ||
            element === ELEMENT.SECOND_TARGET) &&
          section === SECTION.ID
        ) {
          const isFirst = element === ELEMENT.FIRST_TARGET;
          const { operator, id: currentIdArr, type } = currentCriteria[element];
          let filterGT = null,
            filterLT = null;
          let loadingKeyOverride = null;

          if (
            operator === ROLE_OPERATORS.BETWEEN &&
            Array.isArray(currentIdArr)
          ) {
            if (searchContext?.isMin) {
              specificDropdownKey = isFirst
                ? "firstTargetMinIds"
                : "secondTargetMinIds";
              loadingKeyOverride = specificDropdownKey;
              if (currentIdArr[1]) filterLT = currentIdArr[1];
            } else if (searchContext?.isMax) {
              specificDropdownKey = isFirst
                ? "firstTargetMaxIds"
                : "secondTargetMaxIds";
              loadingKeyOverride = specificDropdownKey;
              if (currentIdArr[0]) filterGT = currentIdArr[0];
            }
          } else {
            specificDropdownKey = isFirst
              ? "firstTargetIds"
              : "secondTargetIds";
            loadingKeyOverride = specificDropdownKey;
          }
          options = await fetchTargetIdOptions(
            isFirst,
            query,
            type,
            filterGT,
            filterLT,
            false,
            loadingKeyOverride
          );
          exactOptions = query
            ? await fetchTargetIdOptions(
                isFirst,
                query,
                type,
                filterGT,
                filterLT,
                true,
                loadingKeyOverride
              )
            : [];
          if (query && exactOptions.length) {
            options = mergeExactFirst(exactOptions, options);
          }
        }

        let selectedVals;
        if (element === ELEMENT.ROLE) selectedVals = currentCriteria.role;
        else if (section === SECTION.TYPE)
          selectedVals = currentCriteria[element]?.type;
        else if (section === SECTION.ID) {
          if (
            currentCriteria[element]?.operator === ROLE_OPERATORS.BETWEEN &&
            searchContext
          ) {
            selectedVals = searchContext.isMin
              ? currentCriteria[element]?.id[0]
              : currentCriteria[element]?.id[1];
          } else {
            selectedVals = currentCriteria[element]?.id;
          }
        }

        const mergedOptions = mergeSelectedIntoOptions(options, selectedVals);
        setSelectOptions((prev) => ({
          ...prev,
          [specificDropdownKey]: mergedOptions,
        }));
      },
      300
    );
    return () => debouncedSearchRef.current?.cancel();
  }, [
    currentCriteria,
    fetchRoleOptions,
    fetchFirstTargetTypeOptions,
    fetchSecondTargetTypeOptions,
    fetchTargetIdOptions,
    mergeSelectedIntoOptions,
  ]);

  // Sync criteria list with backend prop
  useEffect(() => {
    if (Array.isArray(roleCriteria)) {
      setCriteriaList(convertBackendToLocalCriteria(roleCriteria));
    } else if (
      roleCriteria &&
      (roleCriteria.operator === "Or" || roleCriteria.operator === "And")
    ) {
      setCriteriaList(convertBackendToLocalCriteria(roleCriteria));
    } else if (
      roleCriteria &&
      typeof roleCriteria === "object" &&
      "role" in roleCriteria
    ) {
      setCriteriaList(convertBackendToLocalCriteria(roleCriteria));
    } else if (roleCriteria) {
      console.warn(
        "[RoleGroupCriteria] Initial roleCriteria prop is not an array or a valid criteria object, received:",
        roleCriteria
      );
      setCriteriaList([]);
    } else {
      setCriteriaList([]);
    }
  }, [roleCriteria, convertBackendToLocalCriteria]);

  // Exchange Keycloak token for roles API token
  useEffect(() => {
    if (keycloakToken) {
      exchangeTokenForRolesToken(keycloakToken)
        .then(setRolesToken)
        .catch((error) =>
          showErrorNotification("Failed to Fetch Roles Token", error.message)
        );
    }
  }, [keycloakToken]);

  // Fetch roles when opening add/edit modal
  useEffect(() => {
    if ((openAddModal || openEditModal) && rolesToken) {
      const selectedRole = openEditModal ? currentCriteria.role : "";
      const loadRoles = async () => {
        const roles = await fetchRoleOptions("");
        setSelectOptions((prev) => ({
          ...prev,
          roles: mergeSelectedIntoOptions(roles, selectedRole),
        }));
      };
      loadRoles();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    rolesToken,
    openAddModal,
    openEditModal,
    fetchRoleOptions,
    mergeSelectedIntoOptions,
  ]);

  // Fetch 1st Target Types when role changes
  useEffect(() => {
    if (!currentCriteria.role || !rolesToken) return;
    fetchFirstTargetTypeOptions("", currentCriteria.role).then((options) => {
      setSelectOptions((d) => ({
        ...d,
        firstTargetTypes: mergeSelectedIntoOptions(
          options,
          currentCriteria.firstTarget.type
        ),
      }));
    });
  }, [
    rolesToken,
    currentCriteria.role,
    currentCriteria.firstTarget.type,
    fetchFirstTargetTypeOptions,
    mergeSelectedIntoOptions,
  ]);

  // Fetch 1st Target IDs and 2nd Target Types when 1st Target Type changes
  useEffect(() => {
    if (
      !currentCriteria.firstTarget.type ||
      !rolesToken ||
      !currentCriteria.role
    )
      return;

    fetchTargetIdOptions(true, "", currentCriteria.firstTarget.type).then(
      (options) => {
        setSelectOptions((d) => ({
          ...d,
          firstTargetIds: mergeSelectedIntoOptions(
            options,
            currentCriteria.firstTarget.id
          ),
        }));
      }
    );

    fetchSecondTargetTypeOptions(
      "",
      currentCriteria.role,
      currentCriteria.firstTarget.type
    ).then((options) => {
      setSelectOptions((d) => ({
        ...d,
        secondTargetTypes: mergeSelectedIntoOptions(
          options,
          currentCriteria.secondTarget.type
        ),
      }));
    });
  }, [
    rolesToken,
    currentCriteria.role,
    currentCriteria.firstTarget.type,
    currentCriteria.firstTarget.id,
    currentCriteria.secondTarget.type,
    fetchTargetIdOptions,
    fetchSecondTargetTypeOptions,
    mergeSelectedIntoOptions,
  ]);

  // Fetch 2nd Target IDs when 2nd Target Type changes
  useEffect(() => {
    if (!currentCriteria.secondTarget.type || !rolesToken) return;
    fetchTargetIdOptions(false, "", currentCriteria.secondTarget.type).then(
      (options) => {
        setSelectOptions((d) => ({
          ...d,
          secondTargetIds: mergeSelectedIntoOptions(
            options,
            currentCriteria.secondTarget.id
          ),
        }));
      }
    );
  }, [
    rolesToken,
    currentCriteria.secondTarget.type,
    currentCriteria.secondTarget.id,
    fetchTargetIdOptions,
    mergeSelectedIntoOptions,
  ]);

  // Fetch BETWEEN operator min/max options for 1st Target ID
  useEffect(() => {
    const { operator, id, type } = currentCriteria.firstTarget;
    if (
      operator === ROLE_OPERATORS.BETWEEN &&
      Array.isArray(id) &&
      type &&
      rolesToken
    ) {
      fetchTargetIdOptions(true, "", type, null, id[1] || null).then(
        (options) => {
          setSelectOptions((d) => ({
            ...d,
            firstTargetMinIds: mergeSelectedIntoOptions(options, id[0]),
          }));
        }
      );
      fetchTargetIdOptions(true, "", type, id[0] || null, null).then(
        (options) => {
          setSelectOptions((d) => ({
            ...d,
            firstTargetMaxIds: mergeSelectedIntoOptions(options, id[1]),
          }));
        }
      );
    }
  }, [
    rolesToken,
    currentCriteria.firstTarget,
    fetchTargetIdOptions,
    mergeSelectedIntoOptions,
  ]);

  // Fetch BETWEEN operator min/max options for 2nd Target ID
  useEffect(() => {
    const { operator, id, type } = currentCriteria.secondTarget;
    if (
      operator === ROLE_OPERATORS.BETWEEN &&
      Array.isArray(id) &&
      type &&
      rolesToken
    ) {
      fetchTargetIdOptions(false, "", type, null, id[1] || null).then(
        (options) => {
          setSelectOptions((d) => ({
            ...d,
            secondTargetMinIds: mergeSelectedIntoOptions(options, id[0]),
          }));
        }
      );
      fetchTargetIdOptions(false, "", type, id[0] || null, null).then(
        (options) => {
          setSelectOptions((d) => ({
            ...d,
            secondTargetMaxIds: mergeSelectedIntoOptions(options, id[1]),
          }));
        }
      );
    }
  }, [
    rolesToken,
    currentCriteria.secondTarget,
    fetchTargetIdOptions,
    mergeSelectedIntoOptions,
  ]);

  // --- Event Handlers ---

  /**
   * Handles search input changes for select components.
   * Updates the search query state and triggers a debounced search for options.
   *
   * @param {string} element - The criteria element (role, firstTarget, secondTarget).
   * @param {string|null} section - The section (type, id) or null.
   * @param {string} searchQuery - The search query string.
   * @param {object} [searchContext] - Additional context for BETWEEN min/max.
   */
  const handleSearchChange = (
    element,
    section,
    searchQuery,
    searchContext = null
  ) => {
    const queryKey = section
      ? `${element}${capitalize(section)}s`
      : `${element}s`;
    let specificQueryKey = queryKey;
    if (section === SECTION.ID && searchContext?.isMin)
      specificQueryKey = `${element}MinIds`;
    if (section === SECTION.ID && searchContext?.isMax)
      specificQueryKey = `${element}MaxIds`;
    setSearchQueries((prev) => {
      const updated = { ...prev, [specificQueryKey]: searchQuery };
      return updated;
    });
    debouncedSearchRef.current?.(element, section, searchQuery, searchContext);
  };

  /**
   * Handles changes to the operator for target ID fields.
   * Resets ID values and validation state as needed.
   *
   * @param {string} element - The criteria element (firstTarget, secondTarget).
   * @param {string} operator - The selected operator.
   */
  const handleTargetOperatorChange = (element, operator) => {
    setCurrentCriteria((prev) => {
      const updated = cloneDeep(prev);
      updated[element].operator = operator;
      const newId =
        operator === ROLE_OPERATORS.IN
          ? []
          : operator === ROLE_OPERATORS.BETWEEN
          ? ["", ""]
          : "";
      updated[element].id = newId;

      const idFieldKey =
        element === ELEMENT.FIRST_TARGET ? "firstTargetId" : "secondTargetId";
      const idMinFieldKey =
        element === ELEMENT.FIRST_TARGET
          ? "firstTargetIdMin"
          : "secondTargetIdMin";
      const idMaxFieldKey =
        element === ELEMENT.FIRST_TARGET
          ? "firstTargetIdMax"
          : "secondTargetIdMax";

      setTouched((t) => ({
        ...t,
        [idFieldKey]: false,
        [idMinFieldKey]: false,
        [idMaxFieldKey]: false,
      }));

      let errorsToUpdate = {
        [idFieldKey]: "",
        [idMinFieldKey]: "",
        [idMaxFieldKey]: "",
      };

      if (operator === ROLE_OPERATORS.BETWEEN) {
        errorsToUpdate[idMinFieldKey] = getValidationErrorForTarget(
          element,
          VALIDATION_TYPE.MIN_ID
        );
        errorsToUpdate[idMaxFieldKey] = getValidationErrorForTarget(
          element,
          VALIDATION_TYPE.MAX_ID
        );
      } else if (operator) {
        errorsToUpdate[idFieldKey] = getValidationErrorForTarget(
          element,
          VALIDATION_TYPE.ID
        );
      }
      setValidationErrors((v) => ({ ...v, ...errorsToUpdate }));
      return updated;
    });
  };

  const EXPERIMENT_SINGLE_VALUE_OPERATORS = [
    ROLE_OPERATORS.EQUALS,
    ROLE_OPERATORS.NOT_EQUALS,
  ];

  const handleRoleOperatorChange = (operation) => {
    setCurrentCriteria((prev) => ({
      ...prev,
      operation: operation || ROLE_OPERATORS.EQUALS,
    }));
  };

  const handleExperimentChange = (field, value) => {
    setCurrentCriteria((prev) => {
      const updated = cloneDeep(prev);
      updated.roleExperiment[field] = value;
      if (field === "programOperation") {
        if (!value) {
          updated.roleExperiment.experimentProgram = [];
        } else if (EXPERIMENT_SINGLE_VALUE_OPERATORS.includes(value)) {
          updated.roleExperiment.experimentProgram =
            updated.roleExperiment.experimentProgram.slice(0, 1);
        }
      }
      if (field === "statusOperation") {
        if (!value) {
          updated.roleExperiment.experimentStatus = [];
        } else if (EXPERIMENT_SINGLE_VALUE_OPERATORS.includes(value)) {
          updated.roleExperiment.experimentStatus =
            updated.roleExperiment.experimentStatus.slice(0, 1);
        }
      }
      return updated;
    });
  };

  /**
   * Handles changes to target values (role, type, id).
   * Clears dependent fields and validation as needed.
   *
   * @param {string} element - The criteria element.
   * @param {string|null} section - The section (type, id) or null.
   * @param {*} value - The new value.
   */
  const handleTargetValuesChange = (element, section = null, value) => {
    const selectKey = section
      ? `${element}${capitalize(section)}s`
      : `${element}s`;

    if (section === SECTION.ID) {
      const op = currentCriteria[element].operator;
      if (
        op === ROLE_OPERATORS.BETWEEN &&
        Array.isArray(currentCriteria[element].id)
      ) {
        let minKey = `${element}MinIds`;
        let maxKey = `${element}MaxIds`;

        setSearchQueries((prev) => {
          if (selectKey === minKey) {
            return { ...prev, [minKey]: "" };
          } else if (selectKey === maxKey) {
            return { ...prev, [maxKey]: "" };
          } else {
            return { ...prev, [minKey]: "", [maxKey]: "" };
          }
        });
      } else {
        setSearchQueries((prev) => ({ ...prev, [selectKey]: "" }));
      }
    } else {
      setSearchQueries((prev) => ({ ...prev, [selectKey]: "" }));
    }

    if (section === SECTION.ID) {
      const op = currentCriteria[element].operator;
      if (
        op === ROLE_OPERATORS.BETWEEN &&
        Array.isArray(currentCriteria[element].id)
      ) {
        const minErrorKey =
          element === ELEMENT.FIRST_TARGET
            ? "firstTargetIdMin"
            : "secondTargetIdMin";
        const maxErrorKey =
          element === ELEMENT.FIRST_TARGET
            ? "firstTargetIdMax"
            : "secondTargetIdMax";
        if (Array.isArray(value)) {
          setValidationErrors((prev) => ({
            ...prev,
            [minErrorKey]: value[0] ? "" : prev[minErrorKey],
            [maxErrorKey]: value[1] ? "" : prev[maxErrorKey],
          }));
        } else {
          if (selectKey.endsWith("MinIds")) {
            setValidationErrors((prev) => ({
              ...prev,
              [minErrorKey]: value ? "" : prev[minErrorKey],
            }));
          } else if (selectKey.endsWith("MaxIds")) {
            setValidationErrors((prev) => ({
              ...prev,
              [maxErrorKey]: value ? "" : prev[maxErrorKey],
            }));
          }
        }
      } else {
        const errorKey =
          element === ELEMENT.FIRST_TARGET ? "firstTargetId" : "secondTargetId";
        setValidationErrors((prev) => ({
          ...prev,
          [errorKey]: "",
        }));
      }
    }

    setCurrentCriteria((prev) => {
      const updated = cloneDeep(prev);

      if (element === ELEMENT.ROLE) {
        updated.role = value || "";
        updated.firstTarget = cloneDeep(EMPTY_ROLE_CRITERIA.firstTarget);
        updated.secondTarget = cloneDeep(EMPTY_ROLE_CRITERIA.secondTarget);
        updated.roleExperiment = cloneDeep(EMPTY_ROLE_CRITERIA.roleExperiment);

        setSelectOptions((d) => ({
          ...d,
          firstTargetTypes: [],
          firstTargetIds: [],
          firstTargetMinIds: [],
          firstTargetMaxIds: [],
          secondTargetTypes: [],
          secondTargetIds: [],
          secondTargetMinIds: [],
          secondTargetMaxIds: [],
        }));
        setSearchQueries((s) => ({
          ...s,
          firstTargetTypes: "",
          firstTargetIds: "",
          firstTargetMinIds: "",
          firstTargetMaxIds: "",
          secondTargetTypes: "",
          secondTargetIds: "",
          secondTargetMinIds: "",
          secondTargetMaxIds: "",
        }));
      } else if (section === SECTION.TYPE) {
        updated[element].type = value || "";
        updated[element].operator = "";
        updated[element].id = "";

        setSelectOptions((d) => ({
          ...d,
          [`${element}Ids`]: [],
          [`${element}MinIds`]: [],
          [`${element}MaxIds`]: [],
        }));
        setSearchQueries((s) => ({
          ...s,
          [`${element}Ids`]: "",
          [`${element}MinIds`]: "",
          [`${element}MaxIds`]: "",
        }));

        if (element === ELEMENT.FIRST_TARGET) {
          updated.secondTarget = cloneDeep(EMPTY_ROLE_CRITERIA.secondTarget);
          setSelectOptions((d) => ({
            ...d,
            secondTargetTypes: [],
            secondTargetIds: [],
            secondTargetMinIds: [],
            secondTargetMaxIds: [],
          }));
          setSearchQueries((s) => ({
            ...s,
            secondTargetTypes: "",
            secondTargetIds: "",
            secondTargetMinIds: "",
            secondTargetMaxIds: "",
          }));
        }

        const newFirstType =
          element === ELEMENT.FIRST_TARGET
            ? value || ""
            : updated.firstTarget.type;
        if (newFirstType !== EXPERIMENT_TYPE) {
          updated.roleExperiment = cloneDeep(
            EMPTY_ROLE_CRITERIA.roleExperiment
          );
        }
      } else if (section === SECTION.ID) {
        const op = currentCriteria[element].operator;
        if (
          op === ROLE_OPERATORS.BETWEEN &&
          Array.isArray(currentCriteria[element].id)
        ) {
          if (Array.isArray(value)) {
            updated[element].id = value;
          } else {
            const isMin = selectKey.endsWith("MinIds");
            const isMax = selectKey.endsWith("MaxIds");
            const arr = Array.isArray(updated[element].id)
              ? [...updated[element].id]
              : ["", ""];
            if (isMin) arr[0] = value;
            else if (isMax) arr[1] = value;
            updated[element].id = arr;
          }
        } else {
          updated[element].id = value;
        }
      }
      return updated;
    });
  };

  /**
   * Handles clicking the edit button for a criteria row.
   * Prepares the edit modal and ensures selected values are present in selects.
   *
   * @param {number} index - The index of the criteria to edit.
   */
  const handleEditButtonClick = (index) => {
    const criteriaToEdit = cloneDeep(criteriaList[index]);
    ["firstTarget", "secondTarget"].forEach((targetKey) => {
      const target = criteriaToEdit[targetKey];
      if (
        target &&
        target.type &&
        (!target.id ||
          (Array.isArray(target.id) && target.id.length === 0) ||
          (Array.isArray(target.id) && target.id.every((v) => !v)))
      ) {
        target.operator = "";
      }
    });
    setCurrentCriteria(criteriaToEdit);
    setEditIndex(index);
    setOpenEditModal(true);
    setTouched(INITIAL_TOUCHED);
    setValidationErrors({});
    setShowSecondTarget(!!criteriaToEdit.secondTarget?.type);

    setSelectOptions((prev) => ({
      ...prev,
      roles: mergeSelectedIntoOptions(prev.roles, criteriaToEdit.role),
      firstTargetTypes: mergeSelectedIntoOptions(
        prev.firstTargetTypes,
        criteriaToEdit.firstTarget.type
      ),
      firstTargetIds: mergeSelectedIntoOptions(
        prev.firstTargetIds,
        criteriaToEdit.firstTarget.id
      ),
      firstTargetMinIds: mergeSelectedIntoOptions(
        prev.firstTargetMinIds,
        Array.isArray(criteriaToEdit.firstTarget.id)
          ? criteriaToEdit.firstTarget.id[0]
          : ""
      ),
      firstTargetMaxIds: mergeSelectedIntoOptions(
        prev.firstTargetMaxIds,
        Array.isArray(criteriaToEdit.firstTarget.id)
          ? criteriaToEdit.firstTarget.id[1]
          : ""
      ),
      secondTargetTypes: mergeSelectedIntoOptions(
        prev.secondTargetTypes,
        criteriaToEdit.secondTarget.type
      ),
      secondTargetIds: mergeSelectedIntoOptions(
        prev.secondTargetIds,
        criteriaToEdit.secondTarget.id
      ),
      secondTargetMinIds: mergeSelectedIntoOptions(
        prev.secondTargetMinIds,
        Array.isArray(criteriaToEdit.secondTarget.id)
          ? criteriaToEdit.secondTarget.id[0]
          : ""
      ),
      secondTargetMaxIds: mergeSelectedIntoOptions(
        prev.secondTargetMaxIds,
        Array.isArray(criteriaToEdit.secondTarget.id)
          ? criteriaToEdit.secondTarget.id[1]
          : ""
      ),
    }));
  };

  /**
   * Handles saving (add/edit) of criteria.
   * Validates all fields and updates the criteria list and backend.
   *
   * @param {boolean} isEdit - True if editing, false if adding.
   */
  const handleUpdateCriteria = (isEdit) => {
    if (!canModifyGroup) return;
    const newTouchedState = { ...touched };
    const newValidationErrorsState = { ...validationErrors };
    let formHasErrors = false;

    newTouchedState.role = true;
    const roleError = getValidationErrorForTarget(
      ELEMENT.ROLE,
      VALIDATION_TYPE.ROLE
    );
    if (roleError) {
      newValidationErrorsState.role = roleError;
      formHasErrors = true;
    } else {
      newValidationErrorsState.role = "";
    }

    ["firstTarget", "secondTarget"].forEach((targetKey) => {
      const target = currentCriteria[targetKey];
      const idFieldKey =
        targetKey === ELEMENT.FIRST_TARGET ? "firstTargetId" : "secondTargetId";
      const idMinFieldKey =
        targetKey === ELEMENT.FIRST_TARGET
          ? "firstTargetIdMin"
          : "secondTargetIdMin";
      const idMaxFieldKey =
        targetKey === ELEMENT.FIRST_TARGET
          ? "firstTargetIdMax"
          : "secondTargetIdMax";

      newTouchedState[idFieldKey] = true;
      newTouchedState[idMinFieldKey] = true;
      newTouchedState[idMaxFieldKey] = true;

      if (target.operator) {
        if (target.operator === ROLE_OPERATORS.BETWEEN) {
          const minError = getValidationErrorForTarget(
            targetKey,
            VALIDATION_TYPE.MIN_ID
          );
          const maxError = getValidationErrorForTarget(
            targetKey,
            VALIDATION_TYPE.MAX_ID
          );
          if (minError) newValidationErrorsState[idMinFieldKey] = minError;
          else newValidationErrorsState[idMinFieldKey] = "";
          if (maxError) newValidationErrorsState[idMaxFieldKey] = maxError;
          else newValidationErrorsState[idMaxFieldKey] = "";
          if (minError || maxError) formHasErrors = true;
        } else {
          const idError = getValidationErrorForTarget(
            targetKey,
            VALIDATION_TYPE.ID
          );
          if (idError) newValidationErrorsState[idFieldKey] = idError;
          else newValidationErrorsState[idFieldKey] = "";
          if (idError) formHasErrors = true;
        }
      } else if (
        target.type &&
        (target.id || (Array.isArray(target.id) && target.id.length > 0))
      ) {
        newValidationErrorsState[idFieldKey] = "Please select an operator.";
        formHasErrors = true;
      } else {
        newValidationErrorsState[idFieldKey] = "";
        newValidationErrorsState[idMinFieldKey] = "";
        newValidationErrorsState[idMaxFieldKey] = "";
      }
    });

    setTouched(newTouchedState);
    setValidationErrors(newValidationErrorsState);

    if (formHasErrors || !isValidCriteria(currentCriteria)) {
      return;
    }

    let updatedCriteriaList;
    if (isEdit && editIndex !== null) {
      updatedCriteriaList = criteriaList.map((item, idx) =>
        idx === editIndex ? currentCriteria : item
      );
    } else {
      updatedCriteriaList = [...criteriaList, currentCriteria];
    }
    setCriteriaList(updatedCriteriaList);
    const backendPayload = convertLocalToBackendCriteria(updatedCriteriaList);
    handleUpdate(backendPayload, true);
    cleanAddEditModalClose(isEdit ? setOpenEditModal : setOpenAddModal);
  };

  const handleRemoveSecondTarget = () => {
    setShowSecondTarget(false);
    setCurrentCriteria((prev) => {
      const updated = cloneDeep(prev);
      updated.secondTarget = cloneDeep(EMPTY_ROLE_CRITERIA.secondTarget);
      return updated;
    });
  };

  /**
   * Handles deletion of selected criteria from the list and backend.
   */
  const handleDeleteCriteria = () => {
    if (!canModifyGroup || !selectedRows.length) return;
    const updatedCriteriaList = criteriaList.filter(
      (_, index) => !selectedRows.includes(index)
    );
    setCriteriaList(updatedCriteriaList);
    const backendPayload = convertLocalToBackendCriteria(updatedCriteriaList);
    handleUpdate(backendPayload, true);
    setSelectedRows([]);
    setOpenDeleteModal(false);
  };

  return (
    <>
      {loading && !openAddModal && !openEditModal ? (
        <Card style={{ textAlign: "center" }}>
          <Spin size="large" />
        </Card>
      ) : (
        <Card
          style={{ marginBottom: 16 }}
          title={
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Typography.Title level={4} style={{ margin: 0 }}>
                <AuditOutlined style={{ marginRight: 8, color: "#bfbfbf" }} />
                Official CERN Roles
              </Typography.Title>
              <Segmented
                size="small"
                value={categoryOperator}
                onChange={onCategoryOperatorChange}
                options={andOrOptions}
                disabled={!canModifyGroup || !roleCriteria}
              />
              <TooltipIcon
                size={16}
                title="Choose how criteria are combined: AND includes only people who match every condition, while OR includes people who match at least one condition. Changing this can significantly widen or narrow who is included."
              />
            </div>
          }
          extra={
            <Space>
              <Button
                color="green"
                variant="solid"
                disabled={!canModifyGroup}
                onClick={() => {
                  cleanAddEditModalClose();
                  setOpenAddModal(true);
                }}
                icon={<PlusOutlined />}
              >
                Add Criteria
              </Button>
            </Space>
          }
        >
          <Table
            loading={loading && !openAddModal && !openEditModal}
            rowKey="key"
            style={{ overflow: "auto" }}
            pagination={false}
            columns={criteriaTableColumns}
            dataSource={criteriaDataSource}
            rowSelection={rowSelection}
            locale={{ emptyText: "No criteria defined" }}
          />
          {criteriaDataSource.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <Button
                type="primary"
                danger
                icon={<DeleteOutlined />}
                disabled={selectedRows.length === 0 || !canModifyGroup}
                onClick={() => setOpenDeleteModal(true)}
              />
            </div>
          )}
        </Card>
      )}
      {/* Add Criteria Modal */}
      <Modal
        open={openAddModal}
        onCancel={() => cleanAddEditModalClose(setOpenAddModal)}
        width={900}
        centered
        title="Add Role Group Criteria"
        footer={
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div>
              {!showSecondTarget ? (
                <Button
                  type="link"
                  icon={<PlusOutlined />}
                  style={{ padding: 0 }}
                  onClick={() => setShowSecondTarget(true)}
                >
                  Add Another Target
                </Button>
              ) : (
                <Button
                  type="link"
                  style={{ padding: 0 }}
                  onClick={handleRemoveSecondTarget}
                >
                  Remove Second Target
                </Button>
              )}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <Button onClick={() => cleanAddEditModalClose(setOpenAddModal)}>
                Cancel
              </Button>
              <Button
                color="green"
                variant="solid"
                onClick={() => handleUpdateCriteria(false)}
              >
                Add
              </Button>
            </div>
          </div>
        }
      >
        <Form layout="vertical">
          {renderTargetInputs(ELEMENT.ROLE)}
          {renderTargetInputs(ELEMENT.FIRST_TARGET, SECTION.TYPE)}
          {renderTargetInputs(ELEMENT.FIRST_TARGET, SECTION.ID)}
          {renderExperimentProgramStatus()}
          {showSecondTarget && (
            <>
              {renderTargetInputs(ELEMENT.SECOND_TARGET, SECTION.TYPE)}
              {renderTargetInputs(ELEMENT.SECOND_TARGET, SECTION.ID)}
            </>
          )}
        </Form>
      </Modal>
      {/* Edit Criteria Modal */}
      <Modal
        open={openEditModal}
        onCancel={() => cleanAddEditModalClose(setOpenEditModal)}
        width={900}
        centered
        title="Edit Role Group Criteria"
        footer={
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div>
              {!showSecondTarget ? (
                <Button
                  type="link"
                  icon={<PlusOutlined />}
                  style={{ padding: 0 }}
                  onClick={() => setShowSecondTarget(true)}
                >
                  Add Another Target
                </Button>
              ) : (
                <Button
                  type="link"
                  style={{ padding: 0 }}
                  onClick={handleRemoveSecondTarget}
                >
                  Remove Second Target
                </Button>
              )}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <Button onClick={() => cleanAddEditModalClose(setOpenEditModal)}>
                Cancel
              </Button>
              <Button
                color="green"
                variant="solid"
                onClick={() => handleUpdateCriteria(true)}
              >
                Save
              </Button>
            </div>
          </div>
        }
      >
        <Form layout="vertical">
          {renderTargetInputs(ELEMENT.ROLE)}
          {renderTargetInputs(ELEMENT.FIRST_TARGET, SECTION.TYPE)}
          {renderTargetInputs(ELEMENT.FIRST_TARGET, SECTION.ID)}
          {renderExperimentProgramStatus()}
          {showSecondTarget && (
            <>
              {renderTargetInputs(ELEMENT.SECOND_TARGET, SECTION.TYPE)}
              {renderTargetInputs(ELEMENT.SECOND_TARGET, SECTION.ID)}
            </>
          )}
        </Form>
      </Modal>
      {/* Delete Confirmation Modal */}
      <Modal
        open={openDeleteModal}
        onCancel={() => setOpenDeleteModal(false)}
        centered
        width={900}
        title={
          <span>
            <DeleteOutlined style={{ marginRight: 8, color: "#ff4d4f" }} />
            Confirm Deletion
          </span>
        }
        footer={[
          <Button key="cancel" onClick={() => setOpenDeleteModal(false)}>
            Cancel
          </Button>,
          <Button
            key="delete"
            type="primary"
            danger
            onClick={handleDeleteCriteria}
          >
            Delete
          </Button>,
        ]}
      >
        <p>Are you sure you want to remove the selected role criteria?</p>
        {selectedRows.length > 0 && (
          <Table
            columns={buildBaseTableColumns(deleteConfirmationDataSource)}
            dataSource={deleteConfirmationDataSource}
            size="small"
            pagination={false}
          />
        )}
      </Modal>
    </>
  );
};

export default RoleGroupCriteria;
